/**
 * HDB Admin — Leads dashboard.
 * -----------------------------
 * Rows come from `portal_leads` (auto-saved by /portal/register as
 * customers type). Anything still `incomplete` is a call/WhatsApp
 * follow-up opportunity. `converted` = they finished signup; `dismissed`
 * = admin marked "no interest".
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
    Loader2, Phone, MessageCircle, StickyNote, X, Check,
    RefreshCw, ChevronRight, Trash2, AlertCircle, Building2, Home,
} from "lucide-react";

const STATUS_TABS = [
    ["incomplete", "To follow up", "amber"],
    ["converted",  "Converted",    "emerald"],
    ["dismissed",  "Dismissed",    "slate"],
    ["",           "All",          "slate"],
];

const relTime = (iso) => {
    if (!iso) return "—";
    const t = new Date(iso).getTime();
    const secs = Math.floor((Date.now() - t) / 1000);
    if (secs < 60) return `${secs}s ago`;
    if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
    if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
    return `${Math.floor(secs / 86400)}d ago`;
};

const stepLabel = (s) => ({
    basics: "Filling name",
    phone:  "Confirming phone",
    mpin:   "Setting MPIN",
    address:"Adding address",
    ready:  "Ready but not submitted",
}[s] || "Just started");

export default function LeadsPage() {
    const [statusTab, setStatusTab] = useState("incomplete");
    const [leads, setLeads] = useState([]);
    const [counts, setCounts] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notingLead, setNotingLead] = useState(null);
    const [noteText, setNoteText] = useState("");
    const [saving, setSaving] = useState(false);
    const [filterMpin, setFilterMpin] = useState("any");     // any | yes | no
    const [filterAddress, setFilterAddress] = useState("any");

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get(`/admin/portal/leads${statusTab ? `?status=${statusTab}` : ""}`);
            setLeads(r.data.leads || []);
            setCounts(r.data.counts || null);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Couldn't load leads");
        } finally { setLoading(false); }
    };

    // eslint-disable-next-line
    useEffect(() => { load(); }, [statusTab]);

    const filtered = useMemo(() => leads.filter(L => {
        if (filterMpin === "yes" && !L.has_mpin) return false;
        if (filterMpin === "no" &&  L.has_mpin) return false;
        if (filterAddress === "yes" && !L.has_address) return false;
        if (filterAddress === "no" &&  L.has_address) return false;
        return true;
    }), [leads, filterMpin, filterAddress]);

    const addFollowUp = async () => {
        if (!noteText.trim()) { toast.error("Type a note first"); return; }
        setSaving(true);
        try {
            await api.post(`/admin/portal/leads/${notingLead.id}/followup`, { note: noteText.trim() });
            toast.success("Follow-up logged");
            setNotingLead(null); setNoteText("");
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Couldn't save note");
        } finally { setSaving(false); }
    };

    const setStatus = async (lead, next) => {
        try {
            await api.patch(`/admin/portal/leads/${lead.id}/status`, { status: next });
            toast.success(next === "dismissed" ? "Lead dismissed" : "Status updated");
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Couldn't update status");
        }
    };

    const deleteLead = async (lead) => {
        if (!window.confirm(`Delete lead for ${lead.name || lead.contact_phone}? This can't be undone.`)) return;
        try {
            await api.delete(`/admin/portal/leads/${lead.id}`);
            toast.success("Lead deleted");
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Couldn't delete");
        }
    };

    return (
        <div data-testid="leads-page" className="space-y-6">
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <h1 className="text-3xl hdb-heading font-black text-slate-900">Leads</h1>
                    <p className="text-slate-500 text-sm mt-1">
                        Partial signups from the register page. Follow up while the interest is fresh.
                    </p>
                </div>
                <button onClick={load} className="hdb-btn-secondary" data-testid="leads-refresh">
                    <RefreshCw size={14} className="inline mr-1"/> Refresh
                </button>
            </div>

            {/* KPI strip */}
            {counts && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="leads-kpis">
                    <Kpi label="To follow up" value={counts.incomplete} tone="amber"/>
                    <Kpi label="Converted"    value={counts.converted}  tone="emerald"/>
                    <Kpi label="Last 24 hrs"  value={counts.last_24h}   tone="sky"/>
                    <Kpi label="Conversion"   value={`${counts.conversion_rate}%`} tone="slate"/>
                </div>
            )}

            {/* Status tabs */}
            <div className="flex items-center gap-2 flex-wrap" data-testid="leads-status-tabs">
                {STATUS_TABS.map(([val, label, tone]) => {
                    const active = statusTab === val;
                    return (
                        <button key={val || "all"} onClick={() => setStatusTab(val)}
                                data-testid={`leads-tab-${val || "all"}`}
                                className={`px-3 py-1.5 rounded-full text-xs font-bold transition-colors border ${
                                    active
                                      ? `bg-${tone}-600 text-white border-${tone}-600`
                                      : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                                }`}>
                            {label}
                            {counts && val && <span className="ml-1 opacity-70">· {counts[val] ?? 0}</span>}
                        </button>
                    );
                })}

                <div className="w-px h-6 bg-slate-200 mx-2"/>

                <Filter label="MPIN" value={filterMpin} setValue={setFilterMpin}
                        options={[["any","Any"],["yes","Set"],["no","Not set"]]}
                        testid="leads-filter-mpin"/>
                <Filter label="Address" value={filterAddress} setValue={setFilterAddress}
                        options={[["any","Any"],["yes","Has address"],["no","No address"]]}
                        testid="leads-filter-address"/>
            </div>

            {/* List */}
            {loading ? (
                <div className="text-slate-500 flex items-center gap-2" data-testid="leads-loading">
                    <Loader2 size={14} className="animate-spin"/> Loading leads…
                </div>
            ) : filtered.length === 0 ? (
                <div className="hdb-card p-8 text-center text-slate-500" data-testid="leads-empty">
                    No {statusTab || ""} leads match these filters.
                </div>
            ) : (
                <div className="space-y-3" data-testid="leads-list">
                    {filtered.map(L => (
                        <LeadRow key={L.id} lead={L}
                                 onNote={() => { setNotingLead(L); setNoteText(""); }}
                                 onDismiss={() => setStatus(L, "dismissed")}
                                 onRevive={() => setStatus(L, "incomplete")}
                                 onDelete={() => deleteLead(L)}
                                 statusTab={statusTab}/>
                    ))}
                </div>
            )}

            {/* Note dialog */}
            {notingLead && (
                <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
                     data-testid="leads-note-modal">
                    <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-5 space-y-4">
                        <div className="flex items-center gap-2">
                            <div className="w-9 h-9 rounded-xl bg-amber-100 grid place-items-center">
                                <StickyNote size={18} className="text-amber-700"/>
                            </div>
                            <div>
                                <div className="font-bold text-slate-900">Add follow-up note</div>
                                <div className="text-xs text-slate-500">
                                    {notingLead.name || "Unnamed"} · +91 {notingLead.contact_phone}
                                </div>
                            </div>
                        </div>
                        <textarea rows={4} value={noteText}
                                  onChange={(e) => setNoteText(e.target.value)}
                                  placeholder='e.g. "Called at 4pm, will call back tomorrow. Wants Business signup."'
                                  className="w-full border-2 border-slate-200 rounded-xl p-3 outline-none focus:border-emerald-500 text-sm"
                                  data-testid="leads-note-textarea"
                                  autoFocus/>
                        <div className="flex justify-end gap-2">
                            <button onClick={() => setNotingLead(null)}
                                    className="hdb-btn-secondary" data-testid="leads-note-cancel">
                                Cancel
                            </button>
                            <button onClick={addFollowUp} disabled={saving}
                                    className="hdb-btn-primary"
                                    data-testid="leads-note-save">
                                {saving ? <><Loader2 size={14} className="inline animate-spin mr-1"/> Saving…</>
                                        : <><Check size={14} className="inline mr-1"/> Save note</>}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function Kpi({ label, value, tone }) {
    return (
        <div className={`hdb-card p-4 border-l-[3px] border-${tone}-500`}>
            <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">{label}</div>
            <div className="text-2xl font-black text-slate-900 mt-1 hdb-mono">{value ?? "—"}</div>
        </div>
    );
}

function Filter({ label, value, setValue, options, testid }) {
    return (
        <div className="flex items-center gap-1.5 text-xs text-slate-500">
            <span>{label}:</span>
            <select value={value} onChange={(e) => setValue(e.target.value)}
                    className="text-xs font-semibold border border-slate-200 rounded-md px-2 py-1 bg-white"
                    data-testid={testid}>
                {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
        </div>
    );
}

function LeadRow({ lead, onNote, onDismiss, onRevive, onDelete, statusTab }) {
    const isBiz = lead.customer_type === "business";
    const phoneRaw = lead.contact_phone || "";
    const waMsg = encodeURIComponent(
        `Hi ${lead.name || "there"}, this is Har Din Bachat. We saw you started signing up for our grocery portal — anything we can help with?`
    );
    const wa = `https://wa.me/91${phoneRaw}?text=${waMsg}`;

    const bar =
        (lead.name ? 25 : 0) + (lead.contact_phone ? 25 : 0) +
        (lead.has_mpin ? 25 : 0) + (lead.has_address ? 25 : 0);

    return (
        <div className="hdb-card p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-4"
             data-testid={`lead-row-${lead.id}`}>
            <div className="w-10 h-10 rounded-xl bg-slate-100 grid place-items-center shrink-0">
                {isBiz ? <Building2 size={18} className="text-slate-600"/>
                       : <Home     size={18} className="text-slate-600"/>}
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-bold text-slate-900 truncate">
                        {lead.name || <span className="text-slate-400">(no name yet)</span>}
                    </div>
                    <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500 px-1.5 py-0.5 rounded bg-slate-100">
                        {isBiz ? "Business" : "Household"}
                    </span>
                    {lead.gstin && <span className="text-[10px] font-bold uppercase text-emerald-700">GSTIN</span>}
                </div>
                <div className="text-xs text-slate-600 mt-0.5 flex items-center gap-2 flex-wrap">
                    <span className="hdb-mono">+91 {phoneRaw}</span>
                    {lead.city && <span>· {lead.city}</span>}
                    {lead.pincode && <span>· {lead.pincode}</span>}
                    <span className="text-slate-400">·</span>
                    <span className="text-slate-500">{stepLabel(lead.step)}</span>
                    <span className="text-slate-400">·</span>
                    <span className="text-slate-500">Started {relTime(lead.created_at)}</span>
                    {(lead.followup_notes || []).length > 0 && (
                        <span className="text-amber-700 font-bold">
                            · {lead.followup_notes.length} follow-up{lead.followup_notes.length > 1 ? "s" : ""}
                        </span>
                    )}
                </div>
                {/* progress bar */}
                <div className="mt-2 h-1.5 rounded-full bg-slate-100 overflow-hidden max-w-[220px]">
                    <div className={`h-full ${bar >= 100 ? "bg-emerald-500" : bar >= 50 ? "bg-amber-500" : "bg-slate-400"}`}
                         style={{ width: `${bar}%` }}/>
                </div>
                <div className="text-[10px] text-slate-400 mt-1 flex items-center gap-2">
                    <span className={lead.has_mpin ? "text-emerald-600 font-bold" : ""}>
                        {lead.has_mpin ? "✓" : "○"} MPIN
                    </span>
                    <span className={lead.has_address ? "text-emerald-600 font-bold" : ""}>
                        {lead.has_address ? "✓" : "○"} Address
                    </span>
                </div>
                {(lead.followup_notes || []).slice(-1).map((n, idx) => (
                    <div key={idx} className="mt-2 text-[11px] p-2 rounded-lg bg-amber-50 border border-amber-200 text-slate-700">
                        <span className="font-bold text-amber-800">Latest note:</span> {n.note}
                        <span className="text-slate-500 ml-2">— {n.by}, {relTime(n.at)}</span>
                    </div>
                ))}
            </div>
            <div className="flex items-center gap-2 flex-wrap shrink-0" data-testid={`lead-actions-${lead.id}`}>
                <a href={`tel:+91${phoneRaw}`} className="hdb-btn-secondary text-xs" data-testid={`lead-call-${lead.id}`}>
                    <Phone size={12} className="inline mr-1"/> Call
                </a>
                <a href={wa} target="_blank" rel="noreferrer"
                   className="hdb-btn-secondary text-xs" data-testid={`lead-wa-${lead.id}`}>
                    <MessageCircle size={12} className="inline mr-1"/> WhatsApp
                </a>
                <button onClick={onNote} className="hdb-btn-secondary text-xs" data-testid={`lead-note-${lead.id}`}>
                    <StickyNote size={12} className="inline mr-1"/> Note
                </button>
                {statusTab === "incomplete" && (
                    <button onClick={onDismiss} className="hdb-btn-secondary text-xs text-slate-500"
                            data-testid={`lead-dismiss-${lead.id}`}>
                        <X size={12} className="inline mr-1"/> Dismiss
                    </button>
                )}
                {statusTab === "dismissed" && (
                    <button onClick={onRevive} className="hdb-btn-secondary text-xs text-emerald-700"
                            data-testid={`lead-revive-${lead.id}`}>
                        <ChevronRight size={12} className="inline mr-1"/> Reopen
                    </button>
                )}
                <button onClick={onDelete} className="text-[11px] text-slate-400 hover:text-red-600"
                        title="Delete permanently"
                        data-testid={`lead-delete-${lead.id}`}>
                    <Trash2 size={12}/>
                </button>
                {lead.status === "converted" && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-emerald-700">
                        <Check size={11}/> Signed up {relTime(lead.converted_at)}
                    </span>
                )}
            </div>
        </div>
    );
}
